import { Component, Inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';

import { BehaviorSubject, filter, Observable, of, Subscription, switchMap } from 'rxjs';
import { MessageService } from 'primeng/api';
import moment from 'moment-timezone';
import { intersection, sumBy } from 'lodash';

import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { AgprComponentStyleEnum } from '../../../Basic/enums/social-analysis-types.enum';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { FileDownloadsApi } from '../../../../api/fileDownloads/file-downloads.api';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { PreferencesModel } from '../../../../Core/Users/models/preferences.model';
import { TakeOverPanelComponent } from '../../../TakeOver/components/TakeOverPanel/take-over-panel.component';

import { ExportSearchCallerTypeEnum } from '../../../MonitorShared/enum/export-search-caller-type.enum';
import { ExportFileTypeEnum } from '../../../MonitorShared/enum/export-file-type.enum';
import { SearchCriteriaQueryModel } from '../../../../api/mentions/model/search-criteria-query.model';
import { UserAppMessageTypeEnum, UserPreferencesService } from '../../../../Account/UserPreferences/services/user-preferences.service';
import { userPermissionsEnum } from '../../../../Core/Users/enums/user-permissions.enum';
import { UserAppPreferencesModel } from '../../../../Core/Users/models/user-app-preferences.model';
import { FileDownloadStatusEnum } from '../../../Forms/models/file-download.model';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { DropdownOptionModel } from '../../../Forms/models/dropdown-option.model';
import { dateFormats } from '../../../../Core/Users/data/date-formats';
import { timeFormatsEnum } from '../../../../Core/Users/enums/time-formats.enum';
import { CurrencyConversionService } from '../../../../Core/Users/services/currency-conversion.service';
import { SearchContextApiModel } from '../../../../Monitor/Shared/models/search-context-api.model';
import { MentionSearchCriteriaModel } from '../../../../Monitor/Shared/models/mention-search-criteria.model';


enum PostExportCategoryEnum {
    DATES = 'DATES',
    POST = 'POST',
    MENTIONS = 'MENTIONS',
    ENGAGEMENT = 'ENGAGEMENT',
    AUDIENCE = 'AUDIENCE',
    LOCATION = 'LOCATION',
    ORGANIZATION = 'ORGANIZATION'
}

interface PostExportCheckboxGroupModel {
    id: PostExportCategoryEnum;
    label: string;
    checked: boolean;
    fields: any[];
    selectedFields: any[];
}

@Component({
    selector: 'agpr-post-export-setup-dialog',
    templateUrl: './post-export-setup-dialog.component.html',
    styleUrls: ['./post-export-setup-dialog.component.scss'],
    standalone: false
})
export class PostExportSetupDialogComponent extends AbstractDialogComponent implements OnInit, OnDestroy {
    readonly id: string = 'postExportSetupDialog';
    readonly agprComponentStyle = AgprComponentStyleEnum;
    readonly buttonTypes = ButtonTypesEnum;
    readonly MAX_PROCESSING_NUMBER = 5;

    @Input() animationTrigger: Observable<FullProfileAnimationModel>;
    @Input() overlayContainer: Element;
    @Input() query: SearchCriteriaQueryModel;
    @Input() mercuryIds: string[];
    @Input() mentionIds: string[];
    @Input() fileName: string;
    @Input() searchCallerType: ExportSearchCallerTypeEnum;
    @Input() format: ExportFileTypeEnum;
    @Input() isSocial: boolean;
    @Input() isUnsavedMentionType: boolean;
    @Input() searchContext: SearchContextApiModel;
    @Input() searchCriteria: MentionSearchCriteriaModel;

    @ViewChild(TakeOverPanelComponent) takeOverPanel: TakeOverPanelComponent;

    allFieldsSelected: boolean = false;
    totalSelectedFields: string[] = [];
    exportCategories: PostExportCheckboxGroupModel[] = [];
    preferences: PreferencesModel;
    initialEmptyFields: boolean = true;
    subscriptions: Subscription[] = [];
    loading = false;
    hasSocialAccess: boolean;
    permissions: userPermissionsEnum[];
    userAppPrefs: UserAppPreferencesModel;
    defaultUserAppColumns: string[] = [];
    totalFieldOptions: number = 0;
    dateFormat: string;
    dateFormatOptions: DropdownOptionModel[];
    twentyFourHourTime: boolean;
    suggestions$: Observable<DropdownOptionModel[]> = new BehaviorSubject<DropdownOptionModel[]>(
        this.currencyConversionService.getCurrencyDropdownOptions()
    );
    currencyOptions: DropdownOptionModel[] = [];
    selectedCurrencyOption: DropdownOptionModel;
    userCurrency: string;
    hasFinancialTimesEnabled: boolean;

    dateData: PostExportCheckboxGroupModel = {
        id: PostExportCategoryEnum.DATES,
        label: 'Social.postFeed.bulkActions.export.checkboxLabel.dates',
        checked: false,
        fields: [],
        selectedFields: []
    };

    mentionContent: PostExportCheckboxGroupModel = {
        id: PostExportCategoryEnum.MENTIONS,
        label: 'Social.postFeed.bulkActions.export.checkboxLabel.mentionContent',
        checked: false,
        fields: [],
        selectedFields: []
    };

    postData: PostExportCheckboxGroupModel = {
        id: PostExportCategoryEnum.POST,
        label: 'Social.postFeed.bulkActions.export.checkboxLabel.postData',
        checked: false,
        fields: [],
        selectedFields: []
    };

    engagementData = {
        id: PostExportCategoryEnum.ENGAGEMENT,
        label: 'Social.postFeed.bulkActions.export.checkboxLabel.engagementData',
        checked: false,
        fields: [],
        selectedFields: []
    };

    audienceData = {
        id: PostExportCategoryEnum.AUDIENCE,
        label: 'Social.postFeed.bulkActions.export.checkboxLabel.audienceData',
        checked: false,
        fields: [],
        selectedFields: []
    };

    location = {
        id: PostExportCategoryEnum.LOCATION,
        label: 'Social.postFeed.bulkActions.export.checkboxLabel.location',
        checked: false,
        fields: [],
        selectedFields: []
    };

    organization = {
        id: PostExportCategoryEnum.ORGANIZATION,
        label: 'Social.postFeed.bulkActions.export.checkboxLabel.organization',
        checked: false,
        fields: [],
        selectedFields: []
    };

    exportLabelToTokenMap: Map<string, string> = new Map([
        ['Published Date', 'Social.postFeed.bulkActions.export.labels.publishedDate'],
        ['Published Time', 'Social.postFeed.bulkActions.export.labels.publishedTime'],
        ['Timezone', 'Social.postFeed.bulkActions.export.labels.timezone'],
        ['Network', 'Social.postFeed.bulkActions.export.labels.network'],
        ['Classification', 'Social.postFeed.bulkActions.export.labels.classification'],
        ['Channel', 'Social.postFeed.bulkActions.export.labels.channel'],
        ['Author', 'Social.postFeed.bulkActions.export.labels.author'],
        ['Handle', 'Social.postFeed.bulkActions.export.labels.handle'],
        ['Title', 'Social.postFeed.bulkActions.export.labels.title'],
        ['Content', 'Social.postFeed.bulkActions.export.labels.content'],
        ['URL', 'Social.postFeed.bulkActions.export.labels.url'],
        ['Media URLs', 'Social.postFeed.bulkActions.export.labels.mediaUrls'],
        ['Language', 'Social.postFeed.bulkActions.export.labels.language'],
        ['Media Type', 'Social.postFeed.bulkActions.export.labels.mediaType'],
        ['Sentiment', 'Social.postFeed.bulkActions.export.labels.sentiment'],
        ['Translated Language', 'Social.postFeed.bulkActions.export.labels.translatedLanguage'],
        ['AVE', 'Social.postFeed.bulkActions.export.labels.ave'],
        ['Impressions', 'Social.postFeed.bulkActions.export.labels.impressions'],
        ['Domain Authority', 'Social.postFeed.bulkActions.export.labels.domainAuthority'],
        ['Engagements', 'Social.postFeed.bulkActions.export.labels.engagements'],
        ['Engagement Rate', 'Social.postFeed.bulkActions.export.labels.engagementRate'],
        ['Engagements - Amplification', 'Social.postFeed.bulkActions.export.labels.amplification'],
        ['Engagements - Conversation', 'Social.postFeed.bulkActions.export.labels.conversation'],
        ['Engagements - Applause', 'Social.postFeed.bulkActions.export.labels.applause'],
        ['Engagements - Views', 'Social.postFeed.bulkActions.export.labels.views'],
        ['Word Count', 'Social.postFeed.bulkActions.export.labels.wordCount'],
        ['Duration', 'Social.postFeed.bulkActions.export.labels.duration'],
        ['Prominence', 'Social.postFeed.bulkActions.export.labels.prominence'],
        ['Reactions', 'Social.postFeed.bulkActions.export.labels.reactions'],
        ['Comments', 'Social.postFeed.bulkActions.export.labels.comments'],
        ['Shares', 'Social.postFeed.bulkActions.export.labels.shares'],
        ['Views', 'Social.postFeed.bulkActions.export.labels.views'],
        ['Continent', 'Social.postFeed.bulkActions.export.labels.continent'],
        ['Country', 'Social.postFeed.bulkActions.export.labels.country'],
        ['Province/State', 'Social.postFeed.bulkActions.export.labels.provinceState'],
        ['County', 'Social.postFeed.bulkActions.export.labels.county'],
        ['City', 'Social.postFeed.bulkActions.export.labels.city'],
        ['Saved Date', 'Social.postFeed.bulkActions.export.labels.savedDate'],
        ['Edited Date', 'Social.postFeed.bulkActions.export.labels.editedDate'],
        ['Folders', 'Social.postFeed.bulkActions.export.labels.folders'],
        ['Tags', 'Social.postFeed.bulkActions.export.labels.tags'],
        ['Tag Group', 'Social.postFeed.bulkActions.export.labels.tagGroup'],
        ['Source Group', 'Social.postFeed.bulkActions.export.labels.sourceGroup'],
        ['Notes', 'Social.postFeed.bulkActions.export.labels.notes'],
        ['Outlet', 'Social.postFeed.bulkActions.export.labels.outlet'],
        ['Headline', 'Social.postFeed.bulkActions.export.labels.headline'],
        ['Coverage Snippet', 'Social.postFeed.bulkActions.export.labels.coverageSnippet'],
        ['Contextual Snippet', 'Social.postFeed.bulkActions.export.labels.contextualSnippet'],
        ['Insight Name', 'Social.postFeed.bulkActions.export.labels.insightName'],
        ['Insight', 'Social.postFeed.bulkActions.export.labels.insight'],
        ['Original URL', 'Social.postFeed.bulkActions.export.labels.originalUrl'],
        ['Image URLs', 'Social.postFeed.bulkActions.export.labels.imageUrls'],
        ['Attribution', 'Social.postFeed.bulkActions.export.labels.attribution'],
        ['SyndicationId', 'Social.postFeed.bulkActions.export.labels.syndicationId'],
    ]);

    tokenToExportLabelMap: Map<string, string> = new Map([
        ['Social.postFeed.bulkActions.export.labels.publishedDate', 'Published Date'],
        ['Social.postFeed.bulkActions.export.labels.publishedTime', 'Published Time'],
        ['Social.postFeed.bulkActions.export.labels.timezone', 'Timezone'],
        ['Social.postFeed.bulkActions.export.labels.network', 'Network'],
        ['Social.postFeed.bulkActions.export.labels.classification', 'Classification'],
        ['Social.postFeed.bulkActions.export.labels.channel', 'Channel'],
        ['Social.postFeed.bulkActions.export.labels.author', 'Author'],
        ['Social.postFeed.bulkActions.export.labels.handle', 'Handle'],
        ['Social.postFeed.bulkActions.export.labels.title', 'Title'],
        ['Social.postFeed.bulkActions.export.labels.content', 'Content'],
        ['Social.postFeed.bulkActions.export.labels.url', 'URL'],
        ['Social.postFeed.bulkActions.export.labels.mediaUrls', 'Media URLs'],
        ['Social.postFeed.bulkActions.export.labels.language', 'Language'],
        ['Social.postFeed.bulkActions.export.labels.mediaType', 'Media Type'],
        ['Social.postFeed.bulkActions.export.labels.sentiment', 'Sentiment'],
        ['Social.postFeed.bulkActions.export.labels.translatedLanguage', 'Translated Language'],
        ['Social.postFeed.bulkActions.export.labels.ave', 'AVE'],
        ['Social.postFeed.bulkActions.export.labels.impressions', 'Impressions'],
        ['Social.postFeed.bulkActions.export.labels.domainAuthority', 'Domain Authority'],
        ['Social.postFeed.bulkActions.export.labels.engagements', 'Engagements'],
        ['Social.postFeed.bulkActions.export.labels.engagementRate', 'Engagement Rate'],
        ['Social.postFeed.bulkActions.export.labels.amplification', 'Engagements - Amplification'],
        ['Social.postFeed.bulkActions.export.labels.conversation', 'Engagements - Conversation'],
        ['Social.postFeed.bulkActions.export.labels.applause', 'Engagements - Applause'],
        ['Social.postFeed.bulkActions.export.labels.views', 'Engagements - Views'],
        ['Social.postFeed.bulkActions.export.labels.wordCount', 'Word Count'],
        ['Social.postFeed.bulkActions.export.labels.duration', 'Duration'],
        ['Social.postFeed.bulkActions.export.labels.prominence', 'Prominence'],
        ['Social.postFeed.bulkActions.export.labels.reactions', 'Reactions'],
        ['Social.postFeed.bulkActions.export.labels.comments', 'Comments'],
        ['Social.postFeed.bulkActions.export.labels.shares', 'Shares'],
        ['Social.postFeed.bulkActions.export.labels.continent', 'Continent'],
        ['Social.postFeed.bulkActions.export.labels.country', 'Country'],
        ['Social.postFeed.bulkActions.export.labels.provinceState', 'Province/State'],
        ['Social.postFeed.bulkActions.export.labels.county', 'County'],
        ['Social.postFeed.bulkActions.export.labels.city', 'City'],
        ['Social.postFeed.bulkActions.export.labels.savedDate', 'Saved Date'],
        ['Social.postFeed.bulkActions.export.labels.editedDate', 'Edited Date'],
        ['Social.postFeed.bulkActions.export.labels.folders', 'Folders'],
        ['Social.postFeed.bulkActions.export.labels.tags', 'Tags'],
        ['Social.postFeed.bulkActions.export.labels.tagGroup', 'Tag Group'],
        ['Social.postFeed.bulkActions.export.labels.sourceGroup', 'Source Group'],
        ['Social.postFeed.bulkActions.export.labels.notes', 'Notes'],
        ['Social.postFeed.bulkActions.export.labels.outlet', 'Outlet'],
        ['Social.postFeed.bulkActions.export.labels.headline', 'Headline'],
        ['Social.postFeed.bulkActions.export.labels.coverageSnippet', 'Coverage Snippet'],
        ['Social.postFeed.bulkActions.export.labels.contextualSnippet', 'Contextual Snippet'],
        ['Social.postFeed.bulkActions.export.labels.insightName', 'Insight Name'],
        ['Social.postFeed.bulkActions.export.labels.insight', 'Insight'],
        ['Social.postFeed.bulkActions.export.labels.originalUrl', 'Original URL'],
        ['Social.postFeed.bulkActions.export.labels.imageUrls', 'Image URLs'],
        ['Social.postFeed.bulkActions.export.labels.attribution', 'Attribution'],
        ['Social.postFeed.bulkActions.export.labels.syndicationId', 'SyndicationId'],
    ]);

    constructor(
        public coreDialogService: CoreDialogService,
        public fileDownloadsApi: FileDownloadsApi,
        public currentUserStateService: CurrentUserStateService,
        public messageService: MessageService,
        public localizationService: LocalizationService,
        private userPreferencesService: UserPreferencesService,
        private currencyConversionService: CurrencyConversionService,
        @Inject(APP_BASE_HREF) public appBaseHref: string
    ) {
        super(coreDialogService, 'PostExportSetup');

        this.dateFormatOptions = dateFormats.map(format => new DropdownOptionModel(this.localizationService.transform(format.name), format.value));
    }

    ngOnInit(): void {
        this.subscriptions.push(
            this.userPreferencesService.getUserAppPreferences().subscribe(userAppPrefs => {
                this.userAppPrefs = userAppPrefs;

                if (this.isSocial) {
                    this.defaultUserAppColumns = this.userAppPrefs.socialPrefs.defaultExportColumns;
                } else {
                    this.defaultUserAppColumns = this.userAppPrefs.monitoringPrefs.defaultExportColumns;
                }
            }),
            this.currentUserStateService.getPermissions().subscribe(
                permissions => {
                    this.permissions = permissions;
                    if (!permissions?.includes(userPermissionsEnum.advancedMonitoringManager) && this.mentionContent?.fields) {
                        this.mentionContent.fields = this.dateData.fields.filter(field => field !== 'Original URL');
                    }
                }
            ),
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x)).subscribe(preferences => {
                this.preferences = preferences;
                this.dateFormat = preferences.dateFormat;
                this.twentyFourHourTime = preferences.timeFormat === timeFormatsEnum.twentyFourHour;
            }),
            this.currentUserStateService.hasSocialAccess().pipe(filter(x => !!x)).subscribe(hasSocialAccess => this.hasSocialAccess = hasSocialAccess),
            this.fileDownloadsApi.getPostExportFields().pipe(filter(x => !!x)).subscribe(fieldGroup => {

                this.location.fields = fieldGroup['locationColumns'].map(field => this.exportLabelToTokenMap.get(field) || field);

                if (this.isSocial) {
                    this.engagementData.fields = fieldGroup['engagementDataColumns'].map(field => this.exportLabelToTokenMap.get(field) || field);
                    this.audienceData.fields = fieldGroup['audienceDataColumns'].map(field => this.exportLabelToTokenMap.get(field) || field);

                    this.postData.fields = fieldGroup['postDataColumns'].map(field => this.exportLabelToTokenMap.get(field) || field);
                    this.postData.label = 'Social.postFeed.bulkActions.export.checkboxLabel.postData';
                    this.location.label = 'Social.postFeed.bulkActions.export.checkboxLabel.location';
                } else {
                    this.mentionContent.fields = fieldGroup['mentionContentColumns'].map(field => this.exportLabelToTokenMap.get(field) || field);
                    this.dateData.fields = fieldGroup['datesColumns'].map(field => this.exportLabelToTokenMap.get(field) || field);

                    this.postData.fields = fieldGroup['mentionDataColumns'].map(field => this.exportLabelToTokenMap.get(field) || field);
                    this.postData.label = 'Social.postFeed.bulkActions.export.checkboxLabel.mentionData';
                    this.location.label = 'Social.postFeed.bulkActions.export.checkboxLabel.locationData';

                    if (!this.permissions?.includes(userPermissionsEnum.advancedMonitoringManager)) {
                        this.mentionContent.fields = this.mentionContent.fields
                            .filter(field => field !== 'Social.postFeed.bulkActions.export.labels.originalUrl');
                    }
                    if (!this.hasSocialAccess) {
                        this.postData.fields =
                            this.postData.fields.filter(field => ![
                                'Social.postFeed.bulkActions.export.labels.engagements',
                                'Social.postFeed.bulkActions.export.labels.engagementRate',
                                'Social.postFeed.bulkActions.export.labels.amplification',
                                'Social.postFeed.bulkActions.export.labels.conversation',
                                'Social.postFeed.bulkActions.export.labels.applause',
                                'Social.postFeed.bulkActions.export.labels.views']
                                .includes(field));
                    }
                }

                if (!this.isUnsavedMentionType) {
                    this.organization.fields = fieldGroup['organizationColumns'];
                }
                // if user prefs is null, default to all selected
                if (!this.defaultUserAppColumns) {
                    this.toggleAllFields();
                } else {
                    this.setColumnsFromUserPrefs();
                }
            }),
            this.currentUserStateService.getUserCurrency().subscribe(userCurrency => {
                this.selectedCurrencyOption = this.currencyConversionService.getCurrencyDropdownOptions().find(option => option.value === userCurrency);
            })
        );
        this.exportCategories = [this.postData, this.engagementData, this.audienceData, this.location, this.organization, this.mentionContent, this.dateData];

        if (!this.fileName) {
            this.fileName = 'Posts - ' + moment().format('YYYY-MM-DD_HH-mm');
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    toggleAllFields(): void {
        this.allFieldsSelected = !this.allFieldsSelected;
        this.initialEmptyFields = false;
        this.exportCategories.forEach(category => {
            category.checked = this.allFieldsSelected;
            this.categoryToggled(category, false);
        });
    }

    categoryToggled(category: PostExportCheckboxGroupModel, updateSelectAllButton = true) {
        this.initialEmptyFields = false;
        if (category.checked && category.fields.length) {
            category.selectedFields = [...category.fields];
        } else {
            category.selectedFields = [];
        }
        this.updateSelectedFields();
        if (updateSelectAllButton) {
            this.updateSelectAllButton();
        }
    }

    fieldToggled(category: any) {
        this.initialEmptyFields = false;
        if (category.selectedFields.length === 0) {
            category.checked = false;
        } else {
            category.checked = true;
        }
        this.updateSelectedFields();
        this.updateSelectAllButton();
    }

    updateSelectedFields(): void {
        this.totalSelectedFields = this.exportCategories.reduce((a, b) => {
            return a.concat(b.selectedFields);
        }, []);
    }

    updateSelectAllButton(): void {
        if (this.totalSelectedFields.length === this.totalFieldOptions) {
            this.allFieldsSelected = true;
        } else {
            this.allFieldsSelected = false;
        }
    }

    removeField(category: PostExportCheckboxGroupModel, index: number): void {
        const targetCategory = this.exportCategories.find(x => x.id === category.id);
        targetCategory.selectedFields.splice(index, 1);
        targetCategory.selectedFields = targetCategory.selectedFields.slice();
        if (targetCategory.selectedFields.length === 0) {
            category.checked = false;
        }
        this.updateSelectedFields();
        this.updateSelectAllButton();
    }

    cancel(): void {
        this.takeOverPanel.close();
    }

    setColumnsFromUserPrefs() {
        this.exportCategories.forEach(category => {
            category.selectedFields = intersection(category.fields, this.defaultUserAppColumns);
            if (category.selectedFields?.length) {
                category.checked = true;
            }
        });

        this.totalFieldOptions = sumBy(this.exportCategories, (category) => {
            return category.fields?.length;
        });

        this.updateSelectedFields();
        this.updateSelectAllButton();
    }

    setDefaultColumns() {
        if (this.userAppPrefs) {
            if (this.isSocial) {
                this.userAppPrefs.socialPrefs.defaultExportColumns = this.totalSelectedFields.slice();
            } else {
                this.userAppPrefs.monitoringPrefs.defaultExportColumns = this.totalSelectedFields.slice();
            }
            this.userPreferencesService.saveChanges(this.userAppPrefs, UserAppMessageTypeEnum.EXPORT).subscribe();
        }
    }

    export(): void {
        this.fileDownloadsApi.getQueuedFiles().pipe(
            switchMap(fileSummaries => {
                if (fileSummaries?.filter(file => file.status === FileDownloadStatusEnum.IN_PROGRESS).length < this.MAX_PROCESSING_NUMBER) {
                    if (fileSummaries.some(file => file.name === this.fileName)) {
                        this.messageService.add({
                            severity: messageSeverityEnum.error,
                            detail: this.localizationService.transform('FileDownloads.error.duplicateFilename', { name: this.fileName })
                        });
                        return of(null);
                    }

                    let languageTag = this.preferences.uiLanguage.languageTag;
                    if (languageTag === 'fr-CA') {
                        languageTag = 'fr-FR';
                    }

                    const request = {
                        name: this.fileName,
                        specification: {
                            query: this.query,
                            mercuryIds: this.mercuryIds,
                            mentionIds: this.mentionIds,
                            columns: this.totalSelectedFields?.map(field => this.tokenToExportLabelMap.get(field) || field),
                            searchCallerType: this.searchCallerType,
                            format: this.format,
                            timeZone: this.preferences.timeZone,
                            dateFormat: this.dateFormat,
                            timeFormat: this.twentyFourHourTime ? 'HH:mm:ss' : 'hh:mm:ss a',
                            currency: this.selectedCurrencyOption.value,
                            searchContext: this.searchContext,
                            showSimilar: this.searchCriteria.showSimilar,
                            language: languageTag,
                        }
                    };
                    return this.fileDownloadsApi.exportPosts(request);
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        detail: this.localizationService.transform('FileDownloads.queueingFileLimitReached', {
                            processingLimit: this.MAX_PROCESSING_NUMBER
                        })
                    });
                    return of(null);
                }
            })
        ).subscribe({
            error: (err) => {
                // for some reason the call returns a 201 and the FE think it's an error
                // we want to handle onCompletion here, because if the call fails, we don't want to open a new window
                if (err?.status === 201) {
                    this.onCompletion();
                } else {
                    console.log('ERR', err);
                }
                this.loading = false;
            }
        });
    }

    onCompletion() {
        window.open(this.appBaseHref + 'FileDownloads', '_blank');
        this.closeDialog(true);
        this.loading = false;
    }
}
